<template>
  <div class="content access-auth">
    <div class="wrapper-section access-auth__wrapper">
      <section class="section access-auth__card">
        <div class="section__content">
          <h1 class="access-auth__title">{{ $t('access.title') }}</h1>
          <p class="access-auth__subtitle">{{ $t('access.subtitle') }}</p>

          <div class="access-auth__types">
            <label class="access-auth__type">
              <input
                v-model="verifyType"
                type="radio"
                value="phone"
              >
              <span>{{ $t('access.type-phone') }}</span>
            </label>
            <label class="access-auth__type">
              <input
                v-model="verifyType"
                type="radio"
                value="order_id"
              >
              <span>{{ $t('access.type-order') }}</span>
            </label>
          </div>

          <label class="access-auth__label">{{ $t('access.code') }}</label>
          <input
            v-model="code"
            class="access-auth__input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            autocomplete="one-time-code"
            :disabled="blocked || loading"
            @input="onCodeInput"
          >

          <p v-if="errorMessage" class="access-auth__error">{{ errorMessage }}</p>
          <p v-else-if="attemptsRemaining !== null && !blocked" class="access-auth__hint">
            {{ $t('access.attempts-remaining', { count: attemptsRemaining }) }}
          </p>

          <t-button
            class="success access-auth__submit"
            :disabled="loading || blocked || code.length !== 4"
            @click="submit"
          >
            {{ $t('access.submit') }}
          </t-button>
        </div>
      </section>
    </div>
    <loader-global></loader-global>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TButton from '@/components/UI/t-button.vue'
import LoaderGlobal from '@/components/_common/loader-global.vue'
import { verifyAccess } from '@/services/accessService'

const props = defineProps({
  orderId: {
    type: [String, Number],
    required: true,
  },
  bypassKey: {
    type: String,
    default: null,
  },
  blocked: {
    type: Boolean,
    default: false,
  },
  blockedRemainingSeconds: {
    type: Number,
    default: 0,
  },
  attemptsRemaining: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['verified'])

const { t } = useI18n()

const verifyType = ref('phone')
const code = ref('')
const loading = ref(false)
const errorMessage = ref('')
const localAttemptsRemaining = ref(props.attemptsRemaining)
const localBlocked = ref(props.blocked)
const localBlockedSeconds = ref(props.blockedRemainingSeconds)

const blocked = computed(() => localBlocked.value || localBlockedSeconds.value > 0)

const attemptsRemaining = computed(() => localAttemptsRemaining.value)

const onCodeInput = () => {
  code.value = code.value.replace(/\D/g, '').slice(0, 4)
  errorMessage.value = ''
}

const applyError = data => {
  if (data.error === 'blocked') {
    localBlocked.value = true
    localBlockedSeconds.value = data.blocked_remaining_seconds || 600
    errorMessage.value = t('access.blocked', {
      minutes: Math.max(1, Math.ceil(localBlockedSeconds.value / 60)),
    })
    return
  }

  if (typeof data.attempts_remaining === 'number') {
    localAttemptsRemaining.value = data.attempts_remaining
  }

  errorMessage.value = t('access.invalid-code')
}

const submit = async () => {
  if (code.value.length !== 4 || blocked.value) {
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    const result = await verifyAccess(props.orderId, {
      code: code.value,
      type: verifyType.value,
      bypassKey: props.bypassKey,
    })

    if (result?.success && result.access_token) {
      emit('verified', result.access_token)
      return
    }

    applyError(result)
  } catch (err) {
    const data = err.response?.data
    if (data) {
      applyError(data)
    } else {
      errorMessage.value = t('access.invalid-code')
    }
  } finally {
    loading.value = false
  }
}
</script>
